/* ══════════════════════════════════════════════════════════════════════
   PAG FILTER BAR — Department / Division / Application(s).

   Options come from ONE call to the main filter API, which returns the
   whole tree nested five deep:

     department -> division -> csp -> application -> environment

   All three are multi-select. Each is narrowed by the ones above it, so
   Division only ever offers divisions that exist under the chosen
   Departments, and Application only apps that exist under the chosen
   Divisions. Empty means "all" at every level.

   Narrowing a parent drops only the child picks the data can no longer
   satisfy, never the whole child selection -- a five-app selection should
   not be wiped because one of them sat outside the new division.

   CSP is walked but never shown. The same appCode appears under every
   cloud an application is deployed on (PAY under AWS and AZURE alike), so
   applications are flattened across csps and deduped by appCode — a
   provider filter would split one application from itself.

   Selections are held as a draft and only applied when "Filter" is
   clicked, so picking a different application does not re-render the
   graph on every change of a dropdown. "Clear" resets both the draft and
   the applied filters in one step.
   ══════════════════════════════════════════════════════════════════════ */
import { useEffect, useMemo, useRef, useState } from "react";
import { Filter as FilterIcon, TriangleAlert, X } from "lucide-react";
import { MultiSelectDropdown } from "../../../components/MultiSelectDropdown";
import {
  fetchPagMainFilter,
  type PagFilterDepartment,
} from "../../../../api/frameworksApi";
import type { PagFilters } from "../data/pagTypes";

const toOptions = (values: string[]) => values.map((v) => ({ value: v, label: v }));

/* Bounded like every CAG filter: a floor so the three read as one row of
   equals, and a ceiling so one long department name cannot push Filter and
   Clear off the end of the bar. */
const FILTER_WIDTH = "min-w-[10rem] max-w-[16rem]";

const uniqueSorted = (values: string[]) => [...new Set(values.filter(Boolean))].sort();

/* ── Tree readers ───────────────────────────────────────────────────────
   Pure functions of the response, so the same narrowing rule drives both
   the visible options and the validity check that clears a stale child. */

/** Divisions under the chosen departments, or across all when none are. */
function divisionsFor(
  tree: PagFilterDepartment[],
  departments: string[],
): string[] {
  const pick = new Set(departments);
  const scope = pick.size
    ? tree.filter((d) => pick.has(d.departmentName))
    : tree;
  return uniqueSorted(
    scope.flatMap((d) => (d.divisions ?? []).map((v) => v.divisionName)),
  );
}

/** Applications under the chosen departments/divisions, flattened across
 *  every csp and deduped by appCode. First appName wins on a clash. */
function applicationsFor(
  tree: PagFilterDepartment[],
  departments: string[],
  divisions: string[],
): { value: string; label: string }[] {
  const byCode = new Map<string, string>();
  const dept = new Set(departments);
  const div = new Set(divisions);

  tree
    .filter((d) => !dept.size || dept.has(d.departmentName))
    .flatMap((d) => d.divisions ?? [])
    .filter((v) => !div.size || div.has(v.divisionName))
    .flatMap((v) => v.cspList ?? [])
    .flatMap((c) => c.applications ?? [])
    .forEach((a) => {
      if (a?.appCode && !byCode.has(a.appCode)) {
        byCode.set(a.appCode, a.appName || a.appCode);
      }
    });

  return [...byCode.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/* ── Component ──────────────────────────────────────────────────────── */

export function PagFilterBar({
  applied,
  onApply,
  onClear,
  onReady,
}: {
  applied: PagFilters;
  onApply: (next: PagFilters) => void;
  onClear: () => void;
  /** Fired once the filter tree has resolved and the default application
   *  has been applied — including when the call fails, so a bad filter
   *  response cannot leave the page waiting forever. The parent holds its
   *  data calls until this fires, which is what keeps the very first
   *  request carrying an appCode instead of an empty list. */
  onReady?: () => void;
}) {
  const [draft, setDraft] = useState<PagFilters>(applied);
  const [tree, setTree] = useState<PagFilterDepartment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* Read inside the one-shot fetch below, which must not re-run when they
     change — hence refs rather than effect dependencies. */
  const appliedRef = useRef(applied);
  appliedRef.current = applied;
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  /* Keeps the draft honest when the parent resets or changes the applied
     filters from elsewhere (e.g. the Clear button). */
  useEffect(() => setDraft(applied), [applied]);

  /* The whole tree arrives in one response, so this runs once — the
     cascade below is derivation, not further requests.

     The first application is applied as the default, so the graphs and the
     review table open scoped to one app rather than the whole estate.
     Applied, not just drafted, so the page loads with data instead of
     waiting for a click on Filter. Skipped if a selection already exists.

     onReady fires last: the parent does not call anything until this
     lands, so the FIRST request already carries this appCode. Without
     that gate every endpoint would fire once with an empty list and then
     again with the default, doubling the load on first paint. */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchPagMainFilter()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : [];
        setTree(list);

        const first = applicationsFor(list, [], [])[0];
        if (first && !appliedRef.current.applications.length) {
          const next = { ...appliedRef.current, applications: [first.value] };
          setDraft(next);
          onApplyRef.current(next);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTree([]);
        setError(e instanceof Error ? e.message : "Could not load the filters");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        onReadyRef.current?.();
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const departments = useMemo(
    () => uniqueSorted(tree.map((d) => d.departmentName)),
    [tree],
  );
  /* Joined for the dependencies: these arrays are rebuilt on every render
     of the draft, so comparing by identity would recompute constantly. */
  const deptKey = draft.departments.join(",");
  const divKey = draft.divisions.join(",");

  const divisions = useMemo(
    () => divisionsFor(tree, draft.departments),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tree, deptKey],
  );
  const applications = useMemo(
    () => applicationsFor(tree, draft.departments, draft.divisions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tree, deptKey, divKey],
  );

  /* Narrowing a parent can strand a child on a value the data no longer
     offers, so each child keeps only the picks that survive. Cascading in
     order matters: divisions are pruned FIRST, because the application
     prune below reads the divisions the department change just left. */
  const set = <K extends keyof PagFilters>(key: K, value: PagFilters[K]) =>
    setDraft((d) => {
      const next = { ...d, [key]: value };

      if (key === "departments" && next.divisions.length) {
        const allowed = new Set(divisionsFor(tree, next.departments));
        next.divisions = next.divisions.filter((v) => allowed.has(v));
      }
      if (
        (key === "departments" || key === "divisions") &&
        next.applications.length
      ) {
        const allowed = new Set(
          applicationsFor(tree, next.departments, next.divisions).map((a) => a.value),
        );
        next.applications = next.applications.filter((code) => allowed.has(code));
      }
      return next;
    });

  /* Sorted before comparing: these are SETS, and the order only records
     which checkbox was clicked first. Unticking a pick and re-ticking it
     moves it to the end of the array, which a raw join would report as a
     change and leave Filter looking armed over an identical selection. */
  const sameSet = (a: string[], b: string[]) =>
    a.length === b.length && [...a].sort().join(",") === [...b].sort().join(",");

  const dirty =
    !sameSet(draft.departments, applied.departments) ||
    !sameSet(draft.divisions, applied.divisions) ||
    !sameSet(draft.applications, applied.applications);

  const hasAny =
    applied.departments.length > 0 ||
    applied.divisions.length > 0 ||
    applied.applications.length > 0 ||
    dirty;

  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* All three follow the CAG filter bar: search, a Select All row, a
            count badge past one pick, a bounded width so a long value cannot
            stretch the bar, and disabled when the level above has left them
            with nothing to offer. */}
        <MultiSelectDropdown
          variant="pill"
          leadingLabel="Department"
          placeholder="All"
          countBadge
          loading={loading}
          searchable
          selectAll
          disabled={!loading && departments.length === 0}
          className={FILTER_WIDTH}
          selected={draft.departments}
          onChange={(v) => set("departments", v)}
          options={toOptions(departments)}
        />
        <MultiSelectDropdown
          variant="pill"
          leadingLabel="Division"
          placeholder="All"
          countBadge
          loading={loading}
          searchable
          selectAll
          disabled={!loading && divisions.length === 0}
          className={FILTER_WIDTH}
          selected={draft.divisions}
          onChange={(v) => set("divisions", v)}
          options={toOptions(divisions)}
        />
        {/* Multi-select: every PAG request body takes an appCodes LIST, so
            the filter matches the contract instead of narrowing it to one.
            Select All ticks every application currently listed — narrowed
            by Department/Division, and by the search box when one is
            typed. Leaving it empty means "All" just the same. */}
        <MultiSelectDropdown
          variant="pill"
          leadingLabel="Application"
          placeholder="All"
          countBadge
          loading={loading}
          searchable
          selectAll
          disabled={!loading && applications.length === 0}
          className={FILTER_WIDTH}
          selected={draft.applications}
          onChange={(v) => set("applications", v)}
          options={applications}
        />

        <button
          onClick={() => onApply(draft)}
          className="inline-flex h-[38px] items-center gap-1.5 rounded-lg bg-indigo-600 px-3.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-500"
        >
          <FilterIcon className="size-3.5" />
          Filter
        </button>

        <button
          onClick={onClear}
          disabled={!hasAny}
          className="inline-flex h-[38px] items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-medium text-foreground/70 transition-colors hover:border-indigo-400/60 hover:text-foreground disabled:cursor-default disabled:opacity-40"
        >
          <X className="size-3.5" />
          Clear
        </button>
      </div>

      {/* A failed filter load leaves every dropdown empty, which on its own
          looks like "no data" rather than "the call failed". */}
      {error && (
        <div className="mt-2 flex items-start gap-1.5">
          <TriangleAlert className="mt-[1px] size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span className="break-words text-[11px] leading-snug text-amber-800 dark:text-amber-200">
            {error}
          </span>
        </div>
      )}
    </div>
  );
}
